import React, {useEffect, useMemo, useRef} from "react";

import {useEllipsisVisible} from "../../../react";
import mergeRefs from "../../../react/utils/mergeRefs";

import {textStyle} from "./styles";
import type {ITextProps} from "./types";

const Text = React.forwardRef<React.ElementRef<"span">, ITextProps>(
    (
        {
            className,
            isTruncated,
            bold,
            underline,
            strikeThrough,
            variant,
            color = "blueGray.600",
            sub,
            italic,
            highlight,
            textTransform,
            numberOfLines,
            tooltipAsTruncated,
            textAlign,
            testID,
            isHeading,
            isParagraph,
            maxW,
            // todo remove as soon as nativebase fully removed
            isUnderlined,
            ...props
        },
        ref
    ) => {
        const textRef = useRef<HTMLSpanElement>(null);
        const [isEllipsisVisible, ellipsisVisibleRef] = useEllipsisVisible(
            !tooltipAsTruncated,
            numberOfLines
        );

        const mergeRef = useMemo(() => {
            if (tooltipAsTruncated) {
                return mergeRefs(ref, textRef, ellipsisVisibleRef);
            }
            return mergeRefs(ref, textRef);
        }, [ref, textRef, ellipsisVisibleRef, tooltipAsTruncated]);

        useEffect(() => {
            if (tooltipAsTruncated && isEllipsisVisible && textRef.current) {
                textRef.current.title = textRef.current.textContent ?? "";
            }
        }, [tooltipAsTruncated, isEllipsisVisible]);

        return (
            <span
                className={textStyle({
                    isTruncated,
                    bold,
                    underline,
                    isUnderlined,
                    strikeThrough,
                    variant,
                    sub,
                    italic,
                    highlight,
                    color,
                    textTransform,
                    numberOfLines,
                    isHeading,
                    isParagraph,
                    maxW,
                    class: className,
                    textAlign
                })}
                {...props}
                ref={mergeRef}
                data-testid={testID}
            />
        );
    }
);

Text.displayName = "Text";

export {Text};
